import React, { useState } from 'react';
import PropTypes from 'prop-types';

import defaultAvatar from '../../assets/avatar.svg';
import colors from '../../styles/colors';
import AvatarSizeType from './AvatarSizes';

import {
  StyledAvatar,
  StyledImage,
  AvatarWrapper,
  StyledBadge,
  OnlineStatus,
} from './styles';
import texts from './texts.json';

const Avatar = ({
  image,
  className,
  name,
  showNewBadge,
  showAIFriendBadge,
  avatarSize,
  isOnline,
  showOnlineStatus,
}) => {
  // Track which url failed rather than a boolean: an instance whose `image` prop changes
  // then renders the new avatar on the first pass. A boolean reset in an effect would
  // paint the previous user's initials for one frame before flipping back.
  const [erroredImage, setErroredImage] = useState(null);

  const renderAvatar = (userName, userImage, cssClassName, size) => {
    // Formats browsers cannot decode (HEIC from iOS uploads), deleted objects and
    // expired tokens all surface as a load error. Fall back to the initials circle.
    if (userImage && erroredImage !== userImage) {
      return (
        <StyledImage
          className={cssClassName}
          src={userImage}
          alt="Avatar"
          $avatarSize={size}
          onError={() => setErroredImage(userImage)}
        />
      );
    }
    if (userName) {
      return (
        <StyledAvatar className={cssClassName} $avatarSize={size}>
          {userName[0].toUpperCase()}
        </StyledAvatar>
      );
    }
    return <StyledImage className={cssClassName} src={defaultAvatar} alt="Avatar" $avatarSize={size} />;
  };

  // Determine which badge to show
  const badgeContent = showAIFriendBadge ? texts.aiFriend : texts.new;
  const invisible = !showAIFriendBadge && !showNewBadge;
  const badgeColor = showAIFriendBadge ? colors.shades.success6 : colors.base.danger;

  return (
    <AvatarWrapper>
      <StyledBadge
        badgeContent={badgeContent}
        invisible={invisible}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        $avatarSize={avatarSize}
        $badgeColor={badgeColor}
        $showNewBadge={showNewBadge}
      >
        {renderAvatar(name, image, className, avatarSize)}
        {showOnlineStatus && <OnlineStatus $isOnline={isOnline} />}
      </StyledBadge>
    </AvatarWrapper>
  );
};

Avatar.propTypes = {
  image: PropTypes.string,
  className: PropTypes.string,
  name: PropTypes.string,
  showNewBadge: PropTypes.bool,
  showAIFriendBadge: PropTypes.bool,
  avatarSize: PropTypes.oneOf(AvatarSizeType),
  isOnline: PropTypes.bool,
  showOnlineStatus: PropTypes.bool,
};

Avatar.defaultProps = {
  image: '',
  className: '',
  name: '',
  showNewBadge: false,
  showAIFriendBadge: false,
  avatarSize: AvatarSizeType.MEDIUM,
  isOnline: false,
  showOnlineStatus: false,
};

export default Avatar;
